import { Component, type ErrorInfo, type ReactNode } from "react";
import { logErrorEvent } from "@/lib/logError";
import {
  isStaleBundleError,
  recoverFromStaleBundle,
} from "@/lib/staleBundleRecovery";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  recovering: boolean;
};

/** Keys we restore the recipe-editor state from. Wiping these on "Start over"
 *  prevents a poison-pill payload from re-crashing the app on every reload. */
const RECIPE_SESSION_KEYS = [
  "cp_recipe",
  "cp_recipe_saved_share_id",
  "cp_recipe_saved_id",
];

/**
 * App-wide error boundary. Without this, an uncaught render error in any
 * route blanks the entire screen — particularly hostile during user
 * testing where a single regression takes the whole site down.
 *
 * We log to `error_events` (rate-limited by the size-guard trigger) and
 * give the user a friendly fallback. "Start over" wipes the in-progress
 * recipe stash so a corrupt payload can't trap the user in a reload loop.
 *
 * Stale-bundle failures (a cached service worker or browser cache pinning
 * the user on an older deploy whose lazy chunks have been deleted) are
 * caught here as well — we surface a brief notice and reload with caches
 * cleared instead of showing the generic error UI.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, recovering: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, recovering: isStaleBundleError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("App error boundary caught:", error, info);
    logErrorEvent({
      code: error.name || "render_error",
      source: "react_boundary",
      message: error.message,
      // Prefer the React component stack — it tells us which component
      // threw, which the JS stack alone usually doesn't.
      stack: info?.componentStack || error.stack || null,
    });

    if (isStaleBundleError(error)) {
      const started = recoverFromStaleBundle();
      if (!started) {
        // Cooldown blocked us — fall back to the manual recovery UI.
        this.setState({ recovering: false });
      }
    }
  }

  handleStartOver = () => {
    if (typeof window === "undefined") return;
    try {
      for (const key of RECIPE_SESSION_KEYS) {
        sessionStorage.removeItem(key);
      }
    } catch {
      // sessionStorage can throw in some incognito modes — non-fatal.
    }
    // Hard reload to "/" so React fully remounts with no leftover state.
    window.location.assign("/");
  };

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.recovering) {
      // recoverFromStaleBundle shows a sonner toast and reloads — render a
      // quiet placeholder in the meantime instead of flashing the generic
      // error UI.
      return (
        <main className="flex min-h-screen items-center justify-center bg-warm-gradient px-6">
          <p className="text-sm text-muted-foreground">Updating to the latest version…</p>
        </main>
      );
    }
    return (
      <main className="min-h-screen bg-warm-gradient">
        <div className="mx-auto flex max-w-md flex-col items-center justify-center gap-4 px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-semibold text-foreground">
            Something went wrong
          </h1>
          <p className="text-sm text-muted-foreground">
            We hit an unexpected error. <strong>Start over</strong> clears the
            current recipe and takes you home — your saved recipes are safe.
          </p>
          <div className="mt-2 flex flex-col items-center gap-2 sm:flex-row">
            <button
              type="button"
              onClick={this.handleStartOver}
              className="inline-flex h-11 items-center justify-center rounded-full bg-primary px-5 text-sm font-medium text-primary-foreground shadow-soft transition-smooth hover:opacity-90"
            >
              Start over
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex h-11 items-center justify-center rounded-full border border-border bg-background px-5 text-sm font-medium text-foreground transition-smooth hover:bg-muted"
            >
              Reload
            </button>
          </div>
        </div>
      </main>
    );
  }
}
